import { useMemo } from 'react';
import { DEAL_STAGES } from '../constants';
import { DealCard } from './DealCard';
import type { Deal, DealStage } from '../types';

interface DealsKanbanProps {
  deals: Deal[];
  onOpen: (deal: Deal) => void;
  onStageChange: (deal: Deal, stage: DealStage) => void;
}

/**
 * Kanban pipeline board — the primary Deals workflow. One column per backend
 * stage. Card values are shown per-deal with their own currency; currency
 * totals live in the PipelineSummary (never mixed).
 */
export function DealsKanban({ deals, onOpen, onStageChange }: DealsKanbanProps) {
  const byStage = useMemo(() => {
    const map: Record<string, Deal[]> = {};
    DEAL_STAGES.forEach((s) => (map[s.key] = []));
    deals.forEach((d) => {
      if (map[d.stage]) map[d.stage].push(d);
    });
    return map;
  }, [deals]);

  return (
    <div className="flex flex-1 gap-3 overflow-x-auto pb-2">
      {DEAL_STAGES.map((stage) => {
        const cards = byStage[stage.key] ?? [];
        return (
          <div
            key={stage.key}
            className="flex w-72 shrink-0 flex-col rounded-card border border-outline bg-surface-container-low"
          >
            <div className="flex items-center justify-between border-b border-outline px-3 py-2">
              <span className="text-label-caps font-semibold uppercase text-on-surface-variant">
                {stage.label}
              </span>
              <span className="rounded-full bg-surface-variant px-2 py-0.5 text-body-sm text-on-surface-variant">
                {cards.length}
              </span>
            </div>
            <div className="flex flex-col gap-2 overflow-y-auto p-2">
              {cards.length === 0 ? (
                <div className="rounded-card border border-dashed border-outline px-2 py-6 text-center text-body-sm text-on-surface-variant/60">
                  No deals
                </div>
              ) : (
                cards.map((deal) => (
                  <DealCard key={deal.id} deal={deal} onOpen={onOpen} onStageChange={onStageChange} />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
